import React from "react"
import { styled } from "@mui/material/styles"
import Grid from "@mui/material/Grid"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"

const FullWidthGrid = styled(Grid)(({ theme }) => ({
  width: '100%',
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}))

const CenteredGrid = styled(Grid)(({ theme }) => ({
  width: "95%",
  justifyContent: "center",
  textAlign: "center",
}))

const CenteredRowGrid = styled(Grid)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
}))

const BoxPadding = styled(Box)(({ theme }) => ({
  paddingLeft: "15px",
  paddingRight: "15px",
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: 10,
  textAlign: "left",
  lineHeight: 1.5,
}))

const SectionHeader = styled(Typography)(({ theme }) => ({
  marginBottom: 6,
  marginTop: 18,
  textAlign: "left",
  fontWeight: 600,
}))

const NerdInfo = () => {
  return (
    <Grid
      data-test-id='top grid'
      container
      spacing={0}
      marginTop="60px"
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <CenteredGrid item>
        <Box>
          <Paper elevation={4} component={FullWidthGrid}>
            <BoxPadding>

              <CenteredRowGrid container>
                <Grid item>
                  <Typography variant="h5">
                    🥚 You Found It
                  </Typography>
                </Grid>
              </CenteredRowGrid>

              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                This is the part of the site that isn't for everyone — just the people who hover
                over lone emojis on About pages to see what happens. Welcome.
              </StyledTypography>

              <SectionHeader variant="subtitle1" color="primary">
                ISCS, Inc.
              </SectionHeader>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                RatingBunny is a project of ISCS, Inc., a small software consultancy I've operated
                for years as the vehicle for independent contract work and personal projects. The
                name stands for Imaging Systems & Consultation Services — which you might see the connection below.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Most of ISCS's work has been backend engineering for large enterprises: financial
                systems, telecom infrastructure, federal healthcare platforms. RatingBunny is the
                other side of that — something built entirely for fun, with no client requirements,
                no sprint ceremonies, and no stakeholders to answer to.
              </StyledTypography>

              <SectionHeader variant="subtitle1" color="primary">
                The Builder's Arc
              </SectionHeader>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                I got my start on Sun Workstations writing medical imaging software for surgical
                and research environments — this was when workstations were genuinely exotic
                hardware and "the web" wasn't a thing yet. From there I moved into the
                broker-dealer world, scripting middleware on open-source tools to customize and
                integrate commercial financial products. Y2K kept everyone busy.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                After that chapter I went deep into web application development — full stack,
                J2EE, JSF, the whole Enterprise Java ceremony. Those large multitiered
                environments got cumbersome fast. Boilerplate everywhere.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                The escape route was reactive frameworks, starting with Play. Decoupling the
                frontend from the backend, leaning into async, ditching the ORM orthodoxy — that
                shift changed how I think about building software. From there it was a natural
                slide into functional paradigms: Scala, ZIO, Cats-Effect, http4s. The kind of
                stack where the types do most of the arguing so the runtime doesn't have to.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Client work pulled me through finance (Morgan Stanley), telecom (Verizon — twice),
                federal contracting (Accenture/healthcare.gov), and most recently a mainframe
                modernization at Capital One. Each engagement added another layer to how I think
                about data pipelines, service boundaries, and keeping systems alive under load.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                RatingBunny is where all of that background goes when there's no client brief in
                the way.
              </StyledTypography>

              <SectionHeader variant="subtitle1" color="primary">
                Under the Hood
              </SectionHeader>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Frontend: React with Material UI. The poster-grid browsing UI was the core design
                goal — get out of list-view mode and let the artwork do the work.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Backend: Async service layer built on functional principles, backed by a NoSQL
                document store. The query model needs to be flexible enough to handle combinations
                of rating ranges, genres, types, and performers without a rigid relational schema
                getting in the way.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Data pipeline: IMDB publishes updated non-commercial dataset files daily. A
                scheduled job pulls, parses, and loads the delta — keeping the corpus current
                without a full reload. The dataset covers title metadata, ratings, and cast/crew
                going back over a century of film.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Infrastructure: Self-hosted on AlmaLinux, behind nginx, with Let's Encrypt for
                TLS. No cloud bill, no vendor lock-in.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                Why not just use the IMDB website? IMDB's own search is optimized for lookup, not
                discovery. There's no clean way to say "show me everything rated 7.8 or above,
                drama, 1970s, directed by someone whose other films I can browse visually." That's
                the gap RatingBunny fills.
              </StyledTypography>

              <SectionHeader variant="subtitle1" color="primary">
                Brown → NYU → Here
              </SectionHeader>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                B.Sc. in Neuropharmacology from Brown, M.S. in Computer Science from NYU. The
                neuropharmacology degree occasionally comes up at parties. The CS degree is more
                load-bearing.
              </StyledTypography>
              <StyledTypography variant="subtitle1" color="primary" paragraph={true}>
                That's the whole nerd egg. If you have questions about the stack or want to trade
                war stories about Enterprise Java, the contact page works for that too.
              </StyledTypography>

            </BoxPadding>
          </Paper>
        </Box>
      </CenteredGrid>
    </Grid>
  )
}

export default NerdInfo
